import { useState, useEffect, useRef } from 'react';
import { BASE_LEAD_TIME_SECONDS } from '../config/playback';

/**
 * 60fps requestAnimationFrame loop with binary search for current word index.
 * Updates currentTime (via playback.setCurrentTime) and tracks currentWordIndex.
 * Auto-skips hidden regions (TOC chapters).
 */
export function useWordTracking(audioRef, manifestData, playback) {
  const { allWords, hiddenRegions } = manifestData;
  const {
    isPlaying,
    currentChunkIndex,
    getChunkStartTime,
    seekingRef,
    seekToGlobalTime,
    setCurrentTime,
  } = playback;

  const [currentWordIndex, setCurrentWordIndex] = useState(-1);
  const animationFrameRef = useRef(null);
  const lastWordIndexRef = useRef(-1);

  useEffect(() => {
    if (!audioRef.current || allWords.length === 0) return;

    const audio = audioRef.current;

    const findWordIndex = (globalTime) => {
      let left = 0;
      let right = allWords.length - 1;
      let best = -1;
      while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        const word = allWords[mid];
        const adjustedTime = globalTime + (BASE_LEAD_TIME_SECONDS * word.leadTimeMultiplier);
        if (adjustedTime >= word.start) {
          best = mid;
          left = mid + 1;
        } else {
          right = mid - 1;
        }
      }
      return best;
    };

    const update = () => {
      // Don't update while a seek is loading new audio — the old audio.currentTime
      // would overwrite the correct seek target with the previous chapter's position.
      if (!audio.paused && !audio.ended && !seekingRef?.current) {
        const globalTime = audio.currentTime + getChunkStartTime(currentChunkIndex);

        // If we've drifted into a hidden region (TOC, illustration list, etc.),
        // jump past it to the next visible chapter.
        const hiddenRegion = hiddenRegions.find(
          r => globalTime >= r.start && globalTime < r.end
        );
        if (hiddenRegion) {
          seekToGlobalTime(hiddenRegion.end);
          animationFrameRef.current = requestAnimationFrame(update);
          return;
        }

        setCurrentTime(globalTime);

        const wordIndex = findWordIndex(globalTime);
        if (wordIndex !== lastWordIndexRef.current) {
          setCurrentWordIndex(wordIndex);
          lastWordIndexRef.current = wordIndex;
        }
      }
      animationFrameRef.current = requestAnimationFrame(update);
    };

    if (isPlaying) {
      animationFrameRef.current = requestAnimationFrame(update);
    }

    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, [allWords, isPlaying, currentChunkIndex, getChunkStartTime, hiddenRegions, seekToGlobalTime, setCurrentTime, audioRef, seekingRef]);

  return { currentWordIndex };
}
